// src/components/LoginScreen.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { APP_NAME } from '../config/appConfig';

const LoginScreen = () => {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    username: '',
    email: '',
    login: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoginMode = mode === 'login';

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError('');
    setIsSubmitting(true);

    try {
      if (isLoginMode) {
        await login({
          login: form.login.trim(),
          password: form.password,
        });
      } else {
        await register({
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
        });
      }
    } catch (err) {
      setError(err.message || t('auth.somethingWentWrong'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError('');
    setForm({
      username: '',
      email: '',
      login: '',
      password: '',
    });
  };

  return (
    <div className="min-h-screen bg-purple-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="px-6 pt-6 pb-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="w-8 h-8 rounded-md bg-purple-600 flex items-center justify-center">
              <i className="fas fa-check text-white text-2xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{APP_NAME}</h1>
          </div>
          <p className="text-sm text-gray-500 mt-7">
            {isLoginMode ? t('auth.signIn') : t('auth.createAccount')}
          </p>
        </div>

        <div className="px-6">
          <div className="grid grid-cols-2 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => switchMode('login')}
              className={`py-2 text-sm font-medium rounded-md transition-colors ${
                isLoginMode ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              {t('auth.login')}
            </button>
            <button
              type="button"
              onClick={() => switchMode('register')}
              className={`py-2 text-sm font-medium rounded-md transition-colors ${
                !isLoginMode ? 'bg-white text-purple-600 shadow-sm' : 'text-gray-600'
              }`}
            >
              {t('auth.register')}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
          {!isLoginMode && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.username')}
                </label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoComplete="username"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoComplete="email"
                  required
                />
              </div>
            </>
          )}

          {isLoginMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.usernameOrEmail')}
              </label>
              <input
                type="text"
                value={form.login}
                onChange={(e) => handleChange('login', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoComplete="username"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.password')}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => handleChange('password', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              autoComplete={isLoginMode ? 'current-password' : 'new-password'}
              required
              minLength={6}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('auth.pleaseWait') : isLoginMode ? t('auth.login') : t('auth.createAccountBtn')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginScreen;
